import { redactParams, redactUrl } from './redact'

// Umami loader. Idempotent: calling load() twice is safe.
//
// Unlike GA4/GTM, Umami is COOKIELESS and ANONYMOUS by design - it sets no
// cookies, stores no persistent visitor id, and retains no personal data
// (unique visitors are a daily-rotating salted hash, never stored raw). Because
// it neither reads nor writes the user's device, the ePrivacy / GDPR Art. 88a
// consent trigger does not fire, so it loads PRE-CONSENT for 100% of traffic -
// it is intentionally NOT behind the consent gate that wraps GA4/GTM.
//
// Hosting is Umami Cloud on the EU region (the workspace is EU-resident, so the
// data stays in the EU). The Cloud script + ingest host is cloud.umami.is for
// all Cloud customers - region routing happens server-side, not via the
// hostname. Disclose it in the privacy/cookie policy as an anonymous processor.
//
// Custom events (BUG-527, owner decision 2026-10-21): a short list of track()
// events is mirrored to Umami in the same pre-consent tier as its page views.
// Each event may only carry the params named for it in UMAMI_EVENTS (IDs,
// slugs, enums, amounts), after redactParams: never an e-mail, name, phone,
// address, free text, order id or order number.

const DEFAULT_UMAMI_WEBSITE_ID = '08b07756-4eb3-41cf-9a21-1c58be931adc'
const DEFAULT_UMAMI_SRC = 'https://cloud.umami.is/script.js'
const SCRIPT_ATTR = 'data-helin-umami'

/** Global name Umami calls before each send (its `data-before-send` hook). */
export const UMAMI_BEFORE_SEND = 'helinUmamiBeforeSend'

type UmamiPayload = Record<string, unknown> & { url?: unknown; referrer?: unknown; data?: unknown }

interface UmamiWindow extends Window {
  [UMAMI_BEFORE_SEND]?: (type: string, payload: UmamiPayload) => UmamiPayload
  umami?: { track?: (name: string, data?: Record<string, unknown>) => unknown }
}

/**
 * Umami's before-send hook: receives (type, payload) for every page view and
 * event and sends whatever it returns. We return the payload with url and
 * referrer passed through redactUrl, so no order id reaches Umami.
 */
export function umamiBeforeSend(_type: string, payload: UmamiPayload): UmamiPayload {
  const out: UmamiPayload = { ...payload }
  if (typeof out.url === 'string') out.url = redactUrl(out.url)
  if (typeof out.referrer === 'string') out.referrer = redactUrl(out.referrer)
  // Event data: the same redaction again, whoever called umami.track().
  if (typeof out.data === 'object' && out.data !== null && !Array.isArray(out.data)) {
    out.data = redactParams(out.data as Record<string, unknown>)
  }
  return out
}

export function getUmamiWebsiteId(): string {
  const fromEnv = (
    import.meta.env.VITE_UMAMI_WEBSITE_ID as string | undefined
  )?.trim()
  return fromEnv && fromEnv.length > 0 ? fromEnv : DEFAULT_UMAMI_WEBSITE_ID
}

export function getUmamiSrc(): string {
  const fromEnv = (import.meta.env.VITE_UMAMI_SRC as string | undefined)?.trim()
  return fromEnv && fromEnv.length > 0 ? fromEnv : DEFAULT_UMAMI_SRC
}

export function isUmamiLoaded(): boolean {
  return document.querySelector(`script[${SCRIPT_ATTR}]`) !== null
}

export function loadUmami(
  websiteId: string = getUmamiWebsiteId(),
  src: string = getUmamiSrc(),
): void {
  if (isUmamiLoaded()) return
  if (!websiteId) return

  // The hook must exist before the tracker boots: it sends the first page
  // view as soon as it loads.
  ;(window as UmamiWindow)[UMAMI_BEFORE_SEND] = umamiBeforeSend

  const script = document.createElement('script')
  // Umami ships `defer` (not async) so the queue of in-page events stays
  // ordered relative to the tracker booting - matches the vendor snippet.
  script.defer = true
  script.src = src
  script.setAttribute('data-website-id', websiteId)
  script.setAttribute('data-before-send', UMAMI_BEFORE_SEND)
  script.setAttribute(SCRIPT_ATTR, websiteId)
  // The tracker is deferred: events from the first render wait in `pending`.
  script.addEventListener('load', flushUmamiQueue)
  document.head.appendChild(script)
}

/**
 * The events Umami gets, each with the only params it may carry. `item_id`
 * comes from the first GA4 item, `revenue` from `value` (Umami's Revenue
 * report reads `revenue` + `currency`).
 */
export const UMAMI_EVENTS: ReadonlyMap<string, readonly string[]> = new Map([
  ['book_now_click', ['salon_id', 'booking_vendor', 'had_preferred']],
  ['booking_handoff', ['salon_id', 'booking_vendor']],
  ['call_salon_click', ['salon_id']],
  ['salon_view', ['salon_id']],
  ['concierge_open', []],
  ['newsletter_signup', ['source']],
  ['contact_form_submit', ['subject']],
  ['view_item', ['item_id']],
  ['add_to_cart', ['item_id', 'value', 'currency']],
  ['begin_checkout', ['value', 'currency']],
  ['add_payment_info', []],
  ['purchase', ['revenue', 'currency']],
])

/** Events kept while the deferred tracker boots; more than this are dropped. */
const MAX_PENDING = 20
let pending: Array<[string, Record<string, unknown> | undefined]> = []

function send(name: string, data: Record<string, unknown> | undefined): boolean {
  const tracker = (window as UmamiWindow).umami
  if (typeof tracker?.track !== 'function') return false
  try {
    tracker.track(name, data)
  } catch {
    // Analytics never breaks the page.
  }
  return true
}

function flushUmamiQueue(): void {
  const queued = pending
  pending = []
  for (const [name, data] of queued) send(name, data)
}

/**
 * Mirror a track() event to Umami (no consent needed, see above). Only the
 * events and params in UMAMI_EVENTS pass, redacted. Returns true when the
 * event was sent or queued for the booting tracker; false when it is not on
 * the list or Umami is not loaded (non-prod builds never load it).
 */
export function umamiTrack(event: string, params: Readonly<Record<string, unknown>>): boolean {
  const allowed = UMAMI_EVENTS.get(event)
  if (!allowed || !isUmamiLoaded()) return false
  const items = params.items
  const first: unknown = Array.isArray(items) ? items[0] : undefined
  const source: Record<string, unknown> = {
    ...params,
    item_id: typeof first === 'object' && first !== null ? (first as Record<string, unknown>).item_id : params.item_id,
    revenue: params.value,
  }
  const picked: Record<string, unknown> = {}
  for (const key of allowed) picked[key] = source[key]
  const clean = redactParams(picked)
  const data = Object.keys(clean).length > 0 ? clean : undefined
  if (!send(event, data) && pending.length < MAX_PENDING) pending.push([event, data])
  return true
}

export function _resetUmamiQueueForTesting(): void {
  pending = []
}
